import React, { useState } from 'react';
import { IoCheckbox, IoSquareOutline } from 'react-icons/io5';
import { useDarkMode } from '../../context/DarkModeContext';

interface PreparationModeProps {
  daysUntilStart: number;
  companyName: string;
}

interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export const PreparationMode: React.FC<PreparationModeProps> = ({ 
  daysUntilStart, 
  companyName 
}) => {
  const { darkMode } = useDarkMode();
  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    { id: '1', text: 'Research company culture and values', completed: false },
    { id: '2', text: 'Set 3 initial learning goals', completed: false },
    { id: '3', text: 'Prepare questions for your first day', completed: false },
    { id: '4', text: 'Review onboarding materials', completed: false },
    { id: '5', text: 'Connect with your future team on LinkedIn', completed: false },
    { id: '6', text: 'Set up your workspace and tools', completed: false },
    { id: '7', text: 'Review the tech stack and key projects', completed: false },
    { id: '8', text: 'Plan your commute or remote setup', completed: false }
  ]);

  const toggleItem = (id: string) => {
    setChecklist(prev => 
      prev.map(item => 
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const completedCount = checklist.filter(item => item.completed).length;
  const progress = (completedCount / checklist.length) * 100;

  const formatCountdown = () => {
    if (daysUntilStart === 0) return 'Starting today! 🎉';
    if (daysUntilStart === 1) return 'Starting tomorrow!';
    if (daysUntilStart <= 7) return `${daysUntilStart} days to go`;
    if (daysUntilStart <= 30) return `${Math.ceil(daysUntilStart / 7)} weeks to go`;
    return `${Math.ceil(daysUntilStart / 30)} months to go`;
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[600px] px-4">
      <div className={`max-w-2xl w-full ${darkMode ? 'bg-gray-800' : 'bg-white'} 
        rounded-xl shadow-lg p-8`}>
        
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Get Ready for {companyName}! 🚀
          </h2>
          <p className={`text-xl ${darkMode ? 'text-purple-400' : 'text-purple-600'} font-semibold`}>
            {formatCountdown()}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Preparation Progress
            </span>
            <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {completedCount} of {checklist.length} completed
            </span>
          </div>
          <div className={`h-3 rounded-full overflow-hidden ${
            darkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`}>
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Checklist */}
        <div className="space-y-3">
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Pre-Internship Checklist
          </h3>
          {checklist.map(item => (
            <button
              key={item.id}
              onClick={() => toggleItem(item.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                darkMode 
                  ? 'hover:bg-gray-700' 
                  : 'hover:bg-gray-50'
              }`}
            >
              {item.completed ? (
                <IoCheckbox className="w-6 h-6 text-green-500 flex-shrink-0" />
              ) : (
                <IoSquareOutline className={`w-6 h-6 flex-shrink-0 ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`} />
              )}
              <span className={`text-left ${
                item.completed 
                  ? darkMode ? 'text-gray-500 line-through' : 'text-gray-500 line-through'
                  : darkMode ? 'text-gray-200' : 'text-gray-700'
              }`}>
                {item.text}
              </span>
            </button>
          ))}
        </div>

        {/* Tips Section */}
        <div className={`mt-8 p-4 rounded-lg ${
          darkMode ? 'bg-gray-700' : 'bg-purple-50'
        }`}>
          <h4 className={`text-sm font-semibold mb-2 ${
            darkMode ? 'text-purple-400' : 'text-purple-700'
          }`}>
            💡 Pro Tips
          </h4>
          <ul className={`text-sm space-y-1 ${
            darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <li>• Use this time to set clear goals for your internship</li>
            <li>• Research recent company news and projects</li>
            <li>• Prepare thoughtful questions to make a great first impression</li>
            <li>• Start building your professional network early</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4">
          <button className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-purple-600 hover:bg-purple-700 text-white'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}>
            Set Learning Goals
          </button>
          <button className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
          }`}>
            Add Custom Tasks
          </button>
        </div>
      </div>
    </div>
  );
};